import {
  type Icon,
  IconBuildingBank,
  IconCalendarExclamation,
  IconFileInvoice,
  IconFolder,
  IconListCheck,
  IconMailExclamation,
  IconUserExclamation,
  IconWallet,
} from '@tabler/icons-react';
import { ExternalMailStatus, TaskStatus } from 'api/models';
import { i18nKeys, type TranslateFunction } from 'locales';
import type { RecentActivities } from 'shared/hooks/use-load-dashboard';

export type ActivityName = keyof RecentActivities;

type Wording =
  | { singularKey: string; pluralKey: string }
  | { sentenceKey: string; nounSingularKey: string; nounPluralKey: string };

export type ActivityDescriptor = { icon: Icon; path: string } & Wording;

export const ACTIVITIES: Record<ActivityName, ActivityDescriptor> = {
  debtorsToHandle: {
    icon: IconUserExclamation,
    path: '/needs-handling/handle-clients',
    singularKey: i18nKeys.ACTIVITIES.CLIENT_TO_PAY_ATTENTION,
    pluralKey: i18nKeys.ACTIVITIES.CLIENTS_TO_PAY_ATTENTION,
  },
  invoicesToTreat: {
    icon: IconFileInvoice,
    path: '/needs-handling/handle-invoices',
    singularKey: i18nKeys.ACTIVITIES.INVOICE_TO_PAY_ATTENTION,
    pluralKey: i18nKeys.ACTIVITIES.INVOICES_TO_PAY_ATTENTION,
  },
  ongoingTasksCount: {
    icon: IconListCheck,
    path: `/tasks?status=${TaskStatus.to_do}`,
    singularKey: i18nKeys.DASHBOARD.ONGOING_TASK_COUNT,
    pluralKey: i18nKeys.DASHBOARD.ONGOING_TASKS_COUNT,
  },
  paymentCount: {
    icon: IconBuildingBank,
    path: '/payments/reconciliation',
    singularKey: i18nKeys.ACTIVITIES.UNPROCESSED_TRANSACTION,
    pluralKey: i18nKeys.ACTIVITIES.UNPROCESSED_TRANSACTIONS,
  },
  unprocessedEmailsCount: {
    icon: IconMailExclamation,
    path: `/mails/inbox?status=${ExternalMailStatus.unprocessed}`,
    singularKey: i18nKeys.DASHBOARD.UNPROCESSED_EMAIL_COUNT,
    pluralKey: i18nKeys.DASHBOARD.UNPROCESSED_EMAILS_COUNT,
  },
  unreadThirdPartyMessagesCount: {
    icon: IconFolder,
    path: '/thirdparty-cases?unread_messages=true',
    sentenceKey: i18nKeys.DASHBOARD.UNREAD_THIRD_PARTY_MESSAGES_COUNT,
    nounSingularKey: i18nKeys.THIRD_PARTY_CASES.ONE,
    nounPluralKey: i18nKeys.THIRD_PARTY_CASES.MANY,
  },
  failedRemindersCount: {
    icon: IconCalendarExclamation,
    path: '/reminders/failed',
    singularKey: i18nKeys.DASHBOARD.FAILED_REMINDER_COUNT,
    pluralKey: i18nKeys.DASHBOARD.FAILED_REMINDERS_COUNT,
  },
  latePaymentPlansCount: {
    icon: IconWallet,
    path: '/payment-plans?page=1&late=true',
    singularKey: i18nKeys.DASHBOARD.LATE_PAYMENT_PLAN_COUNT,
    pluralKey: i18nKeys.DASHBOARD.LATE_PAYMENT_PLANS_COUNT,
  },
};

export const activityHtml = (name: ActivityName, count: number, t: TranslateFunction) => {
  const descriptor = ACTIVITIES[name];

  if ('sentenceKey' in descriptor) {
    const noun = t(count === 1 ? descriptor.nounSingularKey : descriptor.nounPluralKey);

    return t(descriptor.sentenceKey, { pluralizedCount: `${count} ${noun}` });
  }

  return t(count > 1 ? descriptor.pluralKey : descriptor.singularKey, { count });
};
